import type { UrlObject } from 'url';

import { createStyles } from '@mantine/core';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import type { ContestType } from '@betterpool/api-types/contests-service';

import { textStyles } from '~/domains/design-system/Text';
import { IconChevronLeft } from '~/domains/design-system/icons/IconChevronLeft';
import type { LeagueAlias } from '~/domains/common/enums/league-alias.enum';
import dt from '~/domains/contest/domains/gamecenter/testing.const';

import PicksLayoutHeaderMenu from './PicksLayoutHeaderMenu';

const useStyles = createStyles((theme) => ({
  header: {
    height: 72,
    background: theme.colors.gray[9],

    [theme.fn.smallerThan('md')]: {
      height: 48,
    },
  },
  headerContent: {
    display: 'grid',
    gridTemplateColumns: '72px 1fr 72px',
    maxWidth: theme.other.layout.maxWidth + 72 * 2 - theme.other.layout.paddingLg * 2,
    margin: '0 auto',

    [theme.fn.smallerThan('md')]: {
      gridTemplateColumns: '72px 1fr 72px',
    },
  },
  headerCloseLink: {
    width: 72,
    height: 72,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    '@media (hover: hover)': {
      '& svg path': {
        transition: 'stroke 0.1s ease-in-out',
      },

      '&:hover svg path': {
        stroke: theme.colors.blue[3],
      },
    },

    [theme.fn.smallerThan('md')]: {
      width: 48,
      height: 48,
    },
  },
  headerTitleContainer: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  headerTitle: {
    textAlign: 'center',
    color: theme.white,
    ...textStyles['headline-large'],
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    maxWidth: '100%',
  },
  headerSubtitle: {
    ...textStyles['body-medium'],
    color: theme.colors.gray[3],
  },
  headerMenu: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

function GamecenterPicksHeader({
  closeHref,
  isMenuVisible = true,
  subtitle,
  title,
  onCloseClick,
  onEntryWithdrawClick,
  leagueAlias,
  contestType,
  hasAutoPicksEnabled = false,
  onEditAutoPicks,
}: {
  closeHref: string | UrlObject;
  isMenuVisible?: boolean;
  subtitle?: string;
  title?: string;
  onCloseClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  onEntryWithdrawClick?: () => void;
  leagueAlias: LeagueAlias;
  contestType: ContestType;
  hasAutoPicksEnabled?: boolean;
  onEditAutoPicks?: () => void;
}) {
  const { t } = useTranslation('contest');
  const { classes, theme } = useStyles();

  return (
    <div className={classes.header}>
      <div className={classes.headerContent}>
        <Link
          href={closeHref}
          className={classes.headerCloseLink}
          title={t('gamecenter.header.close')}
          onClick={onCloseClick}
          data-test-id={dt.common.picksHeader.closeLink}
        >
          <IconChevronLeft size={24} color={theme.white} />
        </Link>
        <div className={classes.headerTitleContainer}>
          {title && (
            <div
              className={classes.headerTitle}
              data-test-id={dt.common.picksHeader.title}
            >
              {title}
            </div>
          )}
          {!!subtitle && (
            <div
              className={classes.headerSubtitle}
              data-test-id={dt.common.picksHeader.subtitle}
            >
              {subtitle}
            </div>
          )}
        </div>
        {isMenuVisible && (
          <div className={classes.headerMenu}>
            <PicksLayoutHeaderMenu
              onWithdrawEntry={onEntryWithdrawClick}
              leagueAlias={leagueAlias}
              contestType={contestType}
              hasAutoPicksEnabled={hasAutoPicksEnabled}
              onEditAutoPicks={onEditAutoPicks}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default GamecenterPicksHeader;
